import React from "react";
import { useTranslation } from "react-i18next";
import "./HeroNousSommes.css";

/**
 * UniTech Engineering — À propos / Hero premium V2.
 * - FR / EN sur le sélecteur de langue existant (i18next).
 * - Aucun changement de configuration i18n, Menu, routes ou CSS globaux.
 * - Pour intégrer la vraie photo validée, renseigner TEAM_PHOTO_SRC ci-dessous.
 * - Tant que la photo originale n'est pas fournie, affiche un emplacement
 *   identifié comme tel, et jamais un portrait fictif.
 */
const TEAM_PHOTO_SRC = "/images/team_work.png";

const CONTENT = {
  fr: {
    eyebrow: "À PROPOS D’UNITECH ENGINEERING",
    titleStart: "Des technologies",
    titleHighlight: "utiles",
    titleEnd: "aux professionnels de l’ascenseur.",
    lead:
      "UniTech Engineering est une entreprise française qui conçoit des solutions connectées pour aider les professionnels à mieux comprendre leurs équipements, simplifier leurs interventions et moderniser leurs services.",
    contact: "Échanger avec notre équipe",
    solutions: "Découvrir nos solutions",
    solutionsNavigation: "Accéder à nos solutions",
    highlightsLabel: "Nos domaines d’expertise",
    highlights: [
      "Ingénierie électronique & logicielle",
      "Ascenseurs & systèmes connectés"
    ],
    visualEyebrow: "L’INGÉNIERIE AU CONTACT DU TERRAIN",
    visualTitle: "Une équipe d’ingénierie tournée vers le terrain",
    visualPending: "Photographie authentique de l’équipe ou du laboratoire à intégrer",
    photoAlt:
      "Photographie de l'équipe UniTech Engineering dans son environnement de travail"
  },
  en: {
    eyebrow: "ABOUT UNITECH ENGINEERING",
    titleStart: "Technology that is",
    titleHighlight: "useful",
    titleEnd: "for elevator professionals.",
    lead:
      "UniTech Engineering is a French company that develops connected solutions to help professionals better understand their equipment, simplify maintenance work and modernize their services.",
    contact: "Talk to our team",
    solutions: "Explore our solutions",
    solutionsNavigation: "Explore our solutions",
    highlightsLabel: "Our areas of expertise",
    highlights: [
      "Electronics & software engineering",
      "Elevators & connected systems"
    ],
    visualEyebrow: "ENGINEERING CONNECTED TO THE FIELD",
    visualTitle: "An engineering team focused on the field",
    visualPending: "Authentic team or laboratory photograph to be added",
    photoAlt:
      "UniTech Engineering team photographed in its working environment"
  }
};

function ArrowIcon({ diagonal = false }) {
  return diagonal ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
      <path d="M6 18 18 6M8 6h10v10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
      <path d="M4 12h16m-6-6 6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function VisualPlaceholder({ content }) {
  return (
    <div className="about-hero__placeholder" aria-label={content.visualPending}>
      <div className="about-hero__placeholder-mark" aria-hidden="true">
        <svg viewBox="0 0 104 104" fill="none" focusable="false">
          <rect x="25" y="18" width="54" height="68" rx="7" stroke="currentColor" strokeWidth="2" />
          <path d="M35 30h34M35 41h34M35 52h34M35 63h34" stroke="currentColor" strokeWidth="1.7" opacity=".85" />
          <path d="M44 86V72h16v14" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
        </svg>
      </div>
    </div>
  );
}

export default function HeroNousSommes({ teamPhotoSrc = TEAM_PHOTO_SRC }) {
  const { i18n } = useTranslation();
  const locale = (i18n.resolvedLanguage || i18n.language || "fr")
    .split("-")[0]
    .toLowerCase();
  const content = CONTENT[locale] || CONTENT.fr;

  return (
    <section className="about-hero" aria-labelledby="nous-sommes-title">
      <div className="about-hero__container">
        <div className="about-hero__grid">
          <div className="about-hero__copy">
            <p className="about-hero__eyebrow">{content.eyebrow}</p>
            <h1 className="about-hero__title" id="nous-sommes-title">
              {content.titleStart}{" "}
              <span className="about-hero__accent">{content.titleHighlight}</span>{" "}
              {content.titleEnd}
            </h1>
            <p className="about-hero__lead">{content.lead}</p>

            <div className="about-hero__actions">
              <a className="about-hero__action about-hero__action--primary" href="/contact">
                <span>{content.contact}</span>
                <ArrowIcon diagonal />
              </a>

              {/* La section Nos solutions n'est pas encore présente sur À propos.
                  Ce sélecteur mène directement aux deux pages déjà existantes,
                  sans créer de lien mort vers une future ancre. */}
              <details className="about-hero__solutions">
                <summary className="about-hero__action about-hero__action--secondary">
                  <span>{content.solutions}</span>
                  <ArrowIcon />
                </summary>
                <nav className="about-hero__solutions-panel" aria-label={content.solutionsNavigation}>
                  <a href="/openlift">OPENlift</a>
                  <a href="/produits/telealarme-iss">Téléalarme ISS</a>
                </nav>
              </details>
            </div>

            <ul className="about-hero__highlights" aria-label={content.highlightsLabel}>
              {content.highlights.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>

          <figure className="about-hero__figure">
            <div className={`about-hero__visual ${teamPhotoSrc ? "about-hero__visual--photo" : "about-hero__visual--pending"}`}>
              {teamPhotoSrc ? (
                <img
                  className="about-hero__photo"
                  src={teamPhotoSrc}
                  alt={content.photoAlt}
                  loading="eager"
                  decoding="async"
                  fetchPriority="high"
                  width="800"
                  height="740"
                />
              ) : (
                <VisualPlaceholder content={content} />
              )}
              <figcaption className="about-hero__visual-caption">
                <span>{content.visualTitle}</span>
                {!teamPhotoSrc && (
                  <small>{content.visualPending}</small>
                )}
              </figcaption>
            </div>
          </figure>
        </div>
      </div>
    </section>
  );
}
